import { useEffect } from 'react';
import { X, Fuel, Calendar, Gauge, Settings, Zap } from 'lucide-react';
import { useCompare } from '@/context/CompareContext';

const formatPrice = (price) => new Intl.NumberFormat("ro-RO").format(price);
const formatKm = (km) => new Intl.NumberFormat("ro-RO").format(km);

const getFuelLabel = (fuel) => {
  const labels = { benzina: "Benzină", diesel: "Diesel", hibrid: "Hibrid", electric: "Electric" };
  return labels[fuel] || fuel;
};

const getTransmissionLabel = (trans) => {
  const labels = { manuala: "Manuală", automata: "Automată" };
  return labels[trans] || trans;
};

// Get min/max for highlighting
const getHighlight = (values, current, type = 'min') => {
  const numValues = values.filter(v => v !== null && v !== undefined);
  if (numValues.length < 2) return '';
  
  const target = type === 'min' ? Math.min(...numValues) : Math.max(...numValues);
  if (current === target) return type === 'min' ? 'highlight-best' : 'highlight-best';
  return '';
};

export const CompareModal = () => {
  const { isModalOpen, closeModal, compareList, removeFromCompare } = useCompare();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  if (!isModalOpen) return null;

  const prices = compareList.map(c => c.price);
  const years = compareList.map(c => c.year);
  const kms = compareList.map(c => c.kilometers);

  const specs = [
    {
      icon: <span className="text-lg font-bold">€</span>,
      label: 'Preț',
      getValue: (car) => `${formatPrice(car.price)} €`,
      getHighlight: (car) => getHighlight(prices, car.price, 'min'),
    },
    {
      icon: <Calendar className="w-4 h-4" />,
      label: 'An fabricație',
      getValue: (car) => car.year,
      getHighlight: (car) => getHighlight(years, car.year, 'max'),
    },
    {
      icon: <Gauge className="w-4 h-4" />,
      label: 'Kilometraj',
      getValue: (car) => `${formatKm(car.kilometers)} km`,
      getHighlight: (car) => getHighlight(kms, car.kilometers, 'min'),
    },
    {
      icon: <Fuel className="w-4 h-4" />,
      label: 'Combustibil',
      getValue: (car) => getFuelLabel(car.fuel),
      getHighlight: () => '',
    },
    {
      icon: <Settings className="w-4 h-4" />,
      label: 'Transmisie',
      getValue: (car) => getTransmissionLabel(car.transmission),
      getHighlight: () => '',
    },
    {
      icon: <Zap className="w-4 h-4" />,
      label: 'Putere',
      getValue: (car) => car.power ? `${car.power} CP` : '-',
      getHighlight: () => '',
    },
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="compare-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={closeModal}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden bg-[#0f0f0f] rounded-xl border border-[#2a2a2a] animate-modal-in">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2a2a2a]">
          <h2 className="text-xl font-bold text-white">Comparare mașini</h2>
          <button
            onClick={closeModal}
            className="p-2 text-gray-400 hover:text-white hover:bg-[#1a1a1a] rounded-lg transition-colors"
            data-testid="close-compare-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Desktop view - table style */}
          <div className="hidden md:block p-5">
            {/* Car headers */}
            <div className="grid gap-4" style={{ gridTemplateColumns: `180px repeat(${compareList.length}, 1fr)` }}>
              <div></div>
              {compareList.map((car) => (
                <div key={car.id} className="compare-car-header">
                  <div className="relative">
                    <img
                      src={car.images?.[0] || 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=300&h=200&fit=crop'}
                      alt={car.title}
                      className="w-full h-36 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeFromCompare(car.id)}
                      className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="mt-3 font-semibold text-white text-center">{car.title}</h3>
                </div>
              ))}
            </div>

            {/* Specs rows */}
            <div className="mt-6 space-y-1">
              {specs.map((spec, idx) => (
                <div 
                  key={spec.label}
                  className="grid gap-4 py-3 border-b border-[#1a1a1a]"
                  style={{ gridTemplateColumns: `180px repeat(${compareList.length}, 1fr)` }}
                >
                  <div className="flex items-center gap-2 text-gray-400">
                    {spec.icon}
                    <span className="text-sm">{spec.label}</span>
                  </div>
                  {compareList.map((car) => (
                    <div 
                      key={car.id} 
                      className={`text-center font-medium text-white ${spec.getHighlight(car)}`}
                    >
                      {spec.getValue(car)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile view - stacked cards */}
          <div className="md:hidden p-4 space-y-4">
            {compareList.map((car, carIdx) => (
              <div key={car.id} className="compare-mobile-card">
                {/* Card header */}
                <div className="flex items-start gap-3 p-4 border-b border-[#2a2a2a]">
                  <img
                    src={car.images?.[0] || 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=200&h=150&fit=crop'}
                    alt={car.title}
                    className="w-20 h-14 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-white text-sm">{car.title}</h3>
                    <p className="text-lg font-bold text-white mt-1">{formatPrice(car.price)} €</p>
                  </div>
                  <button
                    onClick={() => removeFromCompare(car.id)}
                    className="p-1.5 text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Specs grid */}
                <div className="grid grid-cols-2 gap-3 p-4">
                  {specs.slice(1).map((spec) => (
                    <div key={spec.label} className="flex items-center gap-2">
                      <span className="text-gray-500">{spec.icon}</span>
                      <div>
                        <p className="text-xs text-gray-500">{spec.label}</p>
                        <p className={`text-sm font-medium text-white ${spec.getHighlight(car)}`}>
                          {spec.getValue(car)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Mobile comparison tip */}
            <p className="text-center text-xs text-gray-500 py-2">
              Glisează pentru a compara specificațiile
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
